import React, { useEffect, useState } from "react";
import {
    Box, Typography, Grid, Card, CardContent, CardHeader, CircularProgress,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Divider, Link
} from "@mui/material";
import { Helmet } from "react-helmet";
import ReactMarkdown from "react-markdown";


const BACKEND_HOST = process.env.REACT_APP_BACKEND_HOST;

const ShoppingPage = () => {
    const [stores, setStores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedStore, setSelectedStore] = useState(null);

    useEffect(() => {
        const fetchStores = async () => {
            try {
                const res = await fetch(`${BACKEND_HOST}/stores?sort=name&populate=image`);
                const data = await res.json();
                setStores(data.data || []);
            } catch (e) {
                setStores([]);
            }
            setLoading(false);
        };
        fetchStores();
    }, []);

    const handleOpenDialog = (store) => {
        setSelectedStore(store);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedStore(null);
    };

    // Helper to get attribute from store (Strapi v4 compatible)
    const getAttr = (store, field) => store?.[field];

    return (
        <Box>
            <Helmet>

                <title>Shop for Western Wear and Dance Gear in Austin</title>
                <meta name="description" content="Discover dance-friendly stores in Austin for all your western wear and dance needs." />
                <meta charSet="utf-8"/>
                <meta property="og:description" content="Discover dance-friendly stores in Austin for all your western wear and dance needs."/>
                <meta property="twitter:title" content="Shopping - Austin 2-Step"/>
                <meta property="twitter:description" content="Discover dance-friendly stores in Austin for all your western wear and dance needs."/>
                <link rel="canonical" href="https://austin2step.com/shopping"/>     
            </Helmet>
            <Typography variant="h6" gutterBottom>
                Where to Shop for Dance Gear in Austin
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
                Explore a curated list of stores in Austin that cater to the dance community. Whether you're looking for western wear, dance shoes, or accessories, these shops have you covered.
            </Typography>
            <Box my={2}>
                <Divider />
            </Box>

            <Box align="center">
                <a href="/boot-guide">
                    <img src="/images/boot-guide.gif" alt="Boot Guide" />
                </a>
            </Box>

            <Typography variant="h4" gutterBottom>
                Shopping
            </Typography>
            {loading ? (
                <CircularProgress />
            ) : (
                <Grid container spacing={2}>
                    {stores.map((store, idx) => {
                       
                        const imageUrl =
                            store.image?.data?.[0]?.attributes?.url ||
                            store.image?.[0]?.url ||
                            store.image?.url ||
                            null;
                        return (
                            <Grid item xs={12} md={6} key={store.id || idx}>
                                <Card>
                                    <CardHeader title={store.name} />
                                    {imageUrl && (
                                        <Box
                                            sx={{
                                                width: "100%",
                                                height: 180,
                                                overflow: "hidden",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: "#fafafa"
                                            }}
                                        >
                                            <img
                                                src={imageUrl}
                                                alt={store.name}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        </Box>
                                    )}
                                    <CardContent>
                                        <Typography variant="body2" gutterBottom>
                                            {store.location && <span><strong>Location:</strong> {store.location}<br /></span>}
                                            {store.type && <span><strong>Type:</strong> <Chip size="small" label={store.type} /></span>}
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            onClick={() => handleOpenDialog(store)}
                                            sx={{ mt: 1 }}
                                        >
                                            More Info
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* More Info Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {getAttr(selectedStore, "name")}
                </DialogTitle>
                <DialogContent dividers>
                    {selectedStore && (
                        <>
                            {/* Image */}
                            {(() => {
                                const imageUrl =
                                    selectedStore.image?.data?.[0]?.attributes?.url ||
                                    selectedStore.image?.[0]?.url ||
                                    selectedStore.image?.url ||
                                    null;
                                return imageUrl ? (
                                    <Box mb={2} sx={{ width: "100%", textAlign: "center" }}>
                                        <img
                                            src={imageUrl}
                                            alt={selectedStore.name}
                                            style={{ maxWidth: "100%", maxHeight: 200, objectFit: "cover" }}
                                        />
                                    </Box>
                                ) : null;
                            })()}
                            {/* Address fields */}
                            <Typography variant="body2" gutterBottom>
                                {getAttr(selectedStore, "address") && (
                                    <div><strong>Address:</strong> {getAttr(selectedStore, "address")}</div>
                                )}
                                {getAttr(selectedStore, "city") && (
                                    <div><strong>City:</strong> {getAttr(selectedStore, "city")}</div>
                                )}
                                {getAttr(selectedStore, "state") && (
                                    <div><strong>State:</strong> {getAttr(selectedStore, "state")}</div>
                                )}
                                {getAttr(selectedStore, "zip") && (
                                    <div><strong>Zip:</strong> {getAttr(selectedStore, "zip")}</div>
                                )}
                                {getAttr(selectedStore, "location") && (
                                    <div><strong>Location:</strong> {getAttr(selectedStore, "location")}</div>
                                )}
                                {getAttr(selectedStore, "type") && (
                                    <div><strong>Type:</strong> <Chip size="small" label={getAttr(selectedStore, "type")} /></div>
                                )}
                                {getAttr(selectedStore, "instagram") && (
                                    <div>
                                        <strong>Instagram:</strong>{" "}
                                        <a href={`https://instagram.com/${getAttr(selectedStore, "instagram")}`} target="_blank" rel="noopener noreferrer">
                                            @{getAttr(selectedStore, "instagram")}
                                        </a>
                                    </div>
                                )}
                                {getAttr(selectedStore, "website") && (
                                    <div>
                                        <strong>Website:</strong>{" "}
                                        <a href={getAttr(selectedStore, "website")} target="_blank" rel="noopener noreferrer">
                                            {getAttr(selectedStore, "website")}
                                        </a>
                                    </div>
                                )}
                                {getAttr(selectedStore, "fields") && (
                                    <div>
                                        <strong>Fields:</strong> {Array.isArray(getAttr(selectedStore, "fields")) ? getAttr(selectedStore, "fields").join(", ") : getAttr(selectedStore, "fields")}
                                    </div>
                                )}
                            </Typography>
                            {/* Body */}
                            {getAttr(selectedStore, "body") && (
                                <Box mt={2}>
                                    <Typography variant="subtitle1" gutterBottom>About</Typography>
                                    <Typography variant="body2">
                                        <ReactMarkdown components={{ a: Link }}>{getAttr(selectedStore, "body")}</ReactMarkdown>
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ShoppingPage;